/**
 * Permissive plan validator. Collects every overlap, out-of-area, and
 * connectivity issue but never throws and never short-circuits — the editor
 * lets the user save and undo even with a "broken" plan, and the banner
 * needs the full count.
 *
 * Off-grid buildings (resolved.isOffGrid === true) are skipped defensively;
 * the import parser already drops them, but the validator does the same so
 * a future import path that lets them through wouldn't poison every tile.
 *
 * Footprint resolution mirrors the renderer's: when the catalogue is silent
 * or returns 0×0, treat the building as a single 1×1 tile so it can still
 * collide with neighbours and be flagged for review.
 *
 * Connectivity (road-needing buildings must trace back to the town hall via
 * road tiles) lives in `./connectivity.ts` and is memoised by plan version
 * so it doesn't recompute on every render — see that module for the BFS +
 * cluster-grouping algorithm.
 */
import type { GridRect, Plan, PlanBuilding, ResolvedBuilding } from '$lib/types'
import { checkConnectivity } from './connectivity'

export type CatalogueLookup = (cityentityId: string) => ResolvedBuilding | null

export type ValidationCode = 'overlap' | 'outside-unlocked-area' | 'disconnected'

export interface ValidationError {
  code: ValidationCode
  buildingIds: number[]
  squares: { x: number; y: number }[]
}

export interface ValidationResult {
  ok: boolean
  errors: ValidationError[]
  /** Set of building ids that participate in at least one error — handy for
   *  the renderer's red tint without re-walking the error list. */
  invalidBuildingIds: Set<number>
}

interface FootprintBuilding {
  building: PlanBuilding
  width: number
  height: number
}

export function validatePlan(plan: Plan, lookup: CatalogueLookup): ValidationResult {
  const footprints = collectFootprints(plan, lookup)
  const errors: ValidationError[] = []
  const invalidBuildingIds = new Set<number>()

  // Overlap detection. Build a sparse map keyed by "x,y" → ids on that tile.
  // Each tile with more than one occupant becomes one overlap error listing
  // every id sharing the tile.
  const tileOwners = new Map<string, number[]>()
  for (const f of footprints) {
    for (let dx = 0; dx < f.width; dx++) {
      for (let dy = 0; dy < f.height; dy++) {
        const key = tileKey(f.building.x + dx, f.building.y + dy)
        const list = tileOwners.get(key)
        if (list) list.push(f.building.id)
        else tileOwners.set(key, [f.building.id])
      }
    }
  }
  for (const [key, ids] of tileOwners) {
    if (ids.length < 2) continue
    const square = parseTileKey(key)
    errors.push({
      code: 'overlap',
      buildingIds: dedupe(ids),
      squares: [square],
    })
    for (const id of ids) invalidBuildingIds.add(id)
  }

  // Outside-unlocked-area. For each building, every square it occupies must
  // sit inside at least one unlocked rectangle. A building straddling the
  // edge is invalid even if its origin is inside.
  for (const f of footprints) {
    const offending: { x: number; y: number }[] = []
    for (let dx = 0; dx < f.width; dx++) {
      for (let dy = 0; dy < f.height; dy++) {
        const sx = f.building.x + dx
        const sy = f.building.y + dy
        if (!squareInsideAnyArea(sx, sy, plan.unlockedAreas)) {
          offending.push({ x: sx, y: sy })
        }
      }
    }
    if (offending.length > 0) {
      errors.push({
        code: 'outside-unlocked-area',
        buildingIds: [f.building.id],
        squares: offending,
      })
      invalidBuildingIds.add(f.building.id)
    }
  }

  // Connectivity. One ValidationError per disconnected cluster — `[show]`
  // groups by cluster so the cycle visits every building in cluster A
  // before moving to B. Every disconnected building also contributes to
  // `invalidBuildingIds` for the renderer's red tint.
  const connectivityErrors = checkConnectivity(plan, lookup)
  for (const e of connectivityErrors) {
    errors.push(e)
    for (const id of e.buildingIds) invalidBuildingIds.add(id)
  }

  return { ok: errors.length === 0, errors, invalidBuildingIds }
}

function collectFootprints(plan: Plan, lookup: CatalogueLookup): FootprintBuilding[] {
  const out: FootprintBuilding[] = []
  for (const building of plan.buildings) {
    const resolved = lookup(building.cityentityId)
    if (resolved?.isOffGrid) continue
    const width = resolved && resolved.width > 0 ? resolved.width : 1
    const height = resolved && resolved.height > 0 ? resolved.height : 1
    out.push({ building, width, height })
  }
  return out
}

function squareInsideAnyArea(x: number, y: number, areas: GridRect[]): boolean {
  for (const a of areas) {
    if (x >= a.x && x < a.x + a.width && y >= a.y && y < a.y + a.length) return true
  }
  return false
}

function tileKey(x: number, y: number): string {
  return `${x},${y}`
}

function parseTileKey(key: string): { x: number; y: number } {
  const [x, y] = key.split(',').map(Number)
  return { x, y }
}

function dedupe(ids: number[]): number[] {
  return Array.from(new Set(ids))
}
